import { useState, useEffect, useLayoutEffect, type JSX } from 'react';
import { motion } from 'framer-motion';
import { usePushTransition } from '@/hooks/usePushTransition';
import { useKeepAwake, isKeepAwakeSupported } from '@/hooks/useKeepAwake';
import { toaster } from '@/lib/toaster';
import Header from '@/components/Header';
import IngredientList from '@/components/IngredientList';
import BottomNavBar from '@/components/BottomNavBar';
import ChangePhotoDialog from '@/components/ChangePhotoDialog';
import FloatingAIChefButton from '@/components/FloatingAIChefButton';
import { useQuery } from '@tanstack/react-query';
import { useRecipeById } from '@/lib/recipeService';
import { useAllCollections } from '@/lib/collectionService';
import { CollectionService } from '@/lib/collectionService';
import { useLocation, useRoute } from 'wouter';
import { RecipeClient } from '@/lib/recipeClient';
import { RecipeService } from '@/lib/recipeService';
import { ROUTES } from '@/types';
import { HouseholdClient } from '@/lib/householdClient';
import { useAuth } from '@/hooks/useAuth';
import { trackRecipeView } from '@/lib/analytics';
import type { Household } from '@/types/household';
import { getRecipeDisplayImageUrl } from '@/utils/imageUtils';
import { formatDuration } from '@/utils/formatDuration';
import {
  Box, Button, Text, VStack, HStack, Heading, Image, IconButton,
  Grid, GridItem, Skeleton, useBreakpointValue
} from '@chakra-ui/react';
import { MdPlaylistAdd as AddRecipesIcon } from 'react-icons/md';
import { MdRestaurantMenu as ServingsIcon, MdSchedule as TimeIcon, MdLocalFireDepartment as CookIcon } from 'react-icons/md';
import { MdArrowBack as BackIcon, MdShare as ShareIcon, MdMoreVert as MoreIcon, MdEdit as EditIcon, MdDeleteOutline as DeleteIcon, MdBookmarkAdd as CollectionsIcon, MdCameraAlt as CameraIcon, MdContentCopy as CopyIcon } from 'react-icons/md';
import CollectionsDialog from '@/components/CollectionsDialog';
import { MenuRoot, MenuTrigger } from '@chakra-ui/react';
import HeaderMenu from '@/components/HeaderMenu';
import RecipeActionsMenu from '@/components/RecipeActionsMenu';
import RecipeSourceBadge from '@/components/RecipeSourceBadge';
import { getImportSource } from '@/utils/recipeSource';
import { formatIngredientsForClipboard } from '@/utils/formatIngredients';
import NutritionPanel from '@/components/NutritionPanel';
import CookModeToggle from '@/components/CookModeToggle';

const MotionBox = motion(Box);

class PartialFailureError extends Error {
  constructor() {
    super('add-recipe-failed');
  }
}

export default function RecipeView() {
  const [, navigate] = useLocation();
  const [match, params] = useRoute('/recipes/:id');
  const recipeId = params?.id ?? undefined;

  // Layout-branching component with synchronous Zustand data — see CLAUDE.md for the rule.
  const isMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });

  const { user } = useAuth();
  const [showChangePhoto, setShowChangePhoto] = useState(false);
  const [isCollectionsOpen, setIsCollectionsOpen] = useState(false);
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  const [mobileTab, setMobileTab] = useState<'ingredients' | 'instructions' | 'nutrition'>('ingredients');
  // Cook mode is tied to the recipe it was turned on for, so it's off again after
  // recipe → recipe navigation (same component, new id) without a reset effect
  const [cookModeRecipeId, setCookModeRecipeId] = useState<string | null>(null);
  const cookMode = cookModeRecipeId !== null && cookModeRecipeId === recipeId;
  const setCookMode = (on: boolean) => setCookModeRecipeId(on ? recipeId ?? null : null);
  useKeepAwake(cookMode, {
    onError: () => {
      setCookMode(false);
      toaster.create({ title: "Couldn't keep screen awake", type: 'error' });
    },
  });
  // Hidden on web browsers without the Screen Wake Lock API; always available in the native app
  const canUseCookMode = isKeepAwakeSupported();
  const onCollectionsOpen = () => setIsCollectionsOpen(true);
  const onCollectionsClose = () => setIsCollectionsOpen(false);

  // Reset scroll before paint so the page never appears pre-scrolled. An
  // instant jump (not 'smooth') avoids the visible scroll-up glitch when
  // opening a recipe from a scrolled list.
  useLayoutEffect(() => { window.scrollTo({ top: 0, behavior: 'auto' }); }, [recipeId]);

  useEffect(() => {
    if (recipeId) trackRecipeView(recipeId);
  }, [recipeId]);

  const { recipe = null, isLoading } = useRecipeById(recipeId);

  const { data: household } = useQuery<Household | null>({
    queryKey: ['/households/me'],
    queryFn: () => HouseholdClient.getMyHousehold(),
    staleTime: Infinity,
  });

  // Resolve "Added by" display name from household members
  const addedByName = (() => {
    if (!recipe?.addedBy || !household) return null;
    if (recipe.addedBy === user?.sub) return null; // own recipe — don't show
    const member = household.members.find(m => m.userId === recipe.addedBy);
    return member?.displayName ?? null;
  })();

  const importSource = recipe ? getImportSource(recipe) : null;

  const handleCopyIngredients = () => {
    if (!recipe?.ingredients?.length) return;
    const text = formatIngredientsForClipboard(recipe.ingredients);
    navigator.clipboard.writeText(text).then(() => {
      toaster.create({ title: 'Ingredients copied' });
    }).catch(() => {
      toaster.create({ title: 'Unable to copy to clipboard' });
    });
  };

  const { motionProps, handleBack } = usePushTransition({ ready: recipe !== null });

  const handleDelete = async () => {
    if (!confirm('Delete this recipe?')) return;
    if (!recipeId) return;
    try {
      await RecipeService.delete(recipeId);
      toaster.create({ title: 'Deleted', description: 'Recipe removed' });
      navigate(ROUTES.RECIPES_BASE());
    } catch {
      toaster.create({ title: 'Delete failed', description: 'Try again' });
    }
  };

  const handleShare = async () => {
    if (!recipeId) return;
    try {
      const response = await RecipeClient.shareRecipe(recipeId);
      const windowWithCapacitor = window as Window & { Capacitor?: unknown };
      if ('Share' in window && windowWithCapacitor.Capacitor) {
        try {
          const { Share } = await import('@capacitor/share');
          await Share.share({
            title: recipe?.title || 'Check out this recipe!',
            text: `Check out this recipe: ${recipe?.title}`,
            url: response.shareUrl,
            dialogTitle: 'Share Recipe'
          });
        } catch (_err) {
          try {
            await navigator.clipboard.writeText(response.shareUrl);
            toaster.create({ title: 'Link copied!', description: 'Share link has been copied to clipboard' });
          } catch (_clipErr) {
            toaster.create({ title: 'Share failed', description: 'Unable to copy link to clipboard' });
          }
        }
      } else {
        try {
          await navigator.clipboard.writeText(response.shareUrl);
          toaster.create({ title: 'Link copied!', description: 'Share link has been copied to clipboard' });
        } catch (_clipErr) {
          toaster.create({ title: 'Share failed', description: 'Unable to copy link to clipboard' });
        }
      }
    } catch {
      toaster.create({ title: 'Share failed', description: 'Unable to generate share link' });
    }
  };

  const handleUpdateRating = async (rating: number) => {
    try {
      await RecipeService.updateRating(recipe!.id, rating);
    } catch {
      toaster.create({ title: 'Failed to update rating', description: 'Please try again', type: 'error' });
    }
  };

  const { collections } = useAllCollections();

  const isRecipeInCollection = (c: { recipeIds?: string[] }) => !!c.recipeIds?.includes(recipeId ?? '');

  const [isAddingToCollection, setIsAddingToCollection] = useState(false);

  const addToCollection = async (groupId: string) => {
    if (!recipeId) return;
    setIsAddingToCollection(true);
    try {
      await CollectionService.addRecipes(groupId, [recipeId]);
      toaster.create({ title: 'Added to collection' });
      onCollectionsClose();
      CollectionService.generateIngredients(groupId)
        .catch((error) => { console.error('Failed to generate ingredients:', error); });
    } catch {
      toaster.create({ title: 'Add failed', description: 'Try again' });
    } finally {
      setIsAddingToCollection(false);
    }
  };

  const [isCreatingAndAdding, setIsCreatingAndAdding] = useState(false);

  const createAndAdd = async (name: string) => {
    if (!recipeId) return;
    setIsCreatingAndAdding(true);
    try {
      const newCollection = await CollectionService.create(name);
      let updatedCollection;
      try {
        updatedCollection = await CollectionService.addRecipes(newCollection.id, [recipeId]);
      } catch {
        throw new PartialFailureError();
      }
      toaster.create({ title: `Added to ${updatedCollection.title}` });
      onCollectionsClose();
      CollectionService.generateIngredients(updatedCollection.id)
        .catch((error) => { console.error('Failed to generate ingredients:', error); });
    } catch (error) {
      if (error instanceof PartialFailureError) {
        toaster.create({ title: "Collection created, but we couldn't add the recipe — try adding it from your collections", type: 'error' });
        onCollectionsClose();
      } else {
        toaster.create({ title: "Couldn't create collection — please try again", type: 'error' });
      }
    } finally {
      setIsCreatingAndAdding(false);
    }
  };

  if (!match) return <NotFound navigate={navigate} />;
  if (isMobile === undefined || isLoading) return <Loading />;
  if (!recipe) return <NotFound navigate={navigate} />;

  const imageUrl = getRecipeDisplayImageUrl(recipe);

  return (
    <>
    {/* Bottom clearance for the fixed mobile bottom nav, plus the device safe-area
        inset so content (e.g. the nutrition source footnote) is never clipped behind it. */}
    <MotionBox minH="100vh" bg="bg.canvas" pb={{ base: "calc(80px + env(safe-area-inset-bottom, 0px))", lg: 0 }} {...motionProps}>
      {/* ── Desktop: Recipe Sheet layout ─────────────────── */}
      {!isMobile && <Box borderBottomWidth="1px" borderColor="border.muted" bg="bg.canvas" display="flex">
        {/* Image — 200px wide, fixed height */}
        <Box
          w="200px" minH="220px" flexShrink={0}
          borderRightWidth="1px" borderColor="border.muted"
          overflow="hidden" cursor="pointer" position="relative"
          style={{ background: 'linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)' }}
          onClick={() => setIsImageExpanded(true)}
        >
          {imageUrl ? (
            <Image src={imageUrl} alt={recipe.title} w="100%" h="100%" objectFit="cover" style={{ filter: 'saturate(0.8) brightness(0.92)' }} />
          ) : (
            <Box w="100%" h="100%" style={{ background: 'linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)' }} />
          )}
          {/* Change photo button */}
          <Box
            position="absolute" bottom={2} right={2}
            bg="rgba(0,0,0,0.55)"
            borderRadius="full"
            w="28px" h="28px"
            display="flex" alignItems="center" justifyContent="center"
            cursor="pointer"
            _hover={{ bg: 'rgba(0,0,0,0.75)' }}
            transition="background 0.15s"
            onClick={(e) => { e.stopPropagation(); setShowChangePhoto(true); }}
            title="Change photo"
          >
            <Box color="white" lineHeight="1"><CameraIcon size={14} /></Box>
          </Box>
        </Box>

        {/* Info */}
        <Box flex={1} px={8} pt={6} pb={0} display="flex" flexDirection="column" justifyContent="space-between" minW={0}>
          {/* Top row: meta-info left, action buttons top-right */}
          <Box display="flex" gap={4} alignItems="flex-start">
            <Box flex={1} minW={0}>
              <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.12em" color="text.muted" fontWeight="600" mb={2}>Recipe</Text>
              <Heading
                fontFamily="heading" fontWeight="800" fontSize="3xl"
                color="text.primary" lineHeight="1.05" letterSpacing="-0.02em" mb={3}
              >
                {recipe.title}
              </Heading>
              <HStack gap={3} mb={3} flexWrap="wrap" align="center">
                <HStack gap="1px">
                  {[1, 2, 3, 4, 5].map(i => (
                    <Box
                      key={i}
                      as="button" bg="transparent" border="none" p="1px" cursor="pointer" lineHeight="1"
                      color={i <= Math.floor(recipe.rating || 0) ? '#f5c05a' : '#C8C8C8'}
                      fontSize="18px"
                      transition="transform 0.1s, color 0.1s"
                      _hover={{ transform: 'scale(1.2)', color: '#f5c05a' }}
                      onClick={() => void handleUpdateRating(i)}
                      aria-label={`Rate ${i} star${i !== 1 ? 's' : ''}`}
                    >★</Box>
                  ))}
                </HStack>
                {addedByName && (
                  <>
                    <Box w="3px" h="3px" borderRadius="full" bg="border.muted" flexShrink={0} />
                    <Text fontSize="11px" color="text.muted" fontWeight="400">Added by {addedByName}</Text>
                  </>
                )}
                {importSource && (
                  <>
                    <Box w="3px" h="3px" borderRadius="full" bg="border.muted" flexShrink={0} />
                    <RecipeSourceBadge source={importSource} variant="sidebar" />
                  </>
                )}
              </HStack>
              {recipe.tags && recipe.tags.length > 0 && (
                <HStack gap={2} flexWrap="wrap">
                  {recipe.tags.map((tag: string, i: number) => (
                    <Box key={i} fontSize="10px" fontWeight="500" px="9px" py="3px" borderRadius="full" bg="bg.muted" color="text.muted" border="1px solid" borderColor="border.muted">
                      {tag}
                    </Box>
                  ))}
                </HStack>
              )}
            </Box>

            {/* Action buttons — top right */}
            <HStack gap={1} flexShrink={0}>
              {(
                [
                  { label: 'Share',             icon: <ShareIcon size={14} />,       onClick: () => void handleShare() },
                  { label: 'Add to collection', icon: <CollectionsIcon size={14} />, onClick: onCollectionsOpen },
                  { label: 'Edit',              icon: <EditIcon size={14} />,        onClick: () => window.location.assign(`/recipes/${recipe.id}/edit`) },
                  { label: 'Delete',            icon: <DeleteIcon size={14} />,      onClick: () => void handleDelete() },
                ] as const
              ).map(({ label, icon, onClick }) => (
                <Button
                  key={label}
                  variant="ghost"
                  size="sm"
                  color="text.muted"
                  _hover={{ bg: 'bg.muted', color: 'text.primary' }}
                  fontFamily="body" fontWeight="500" fontSize="12px"
                  h="30px" px={2} borderRadius="md"
                  onClick={onClick}
                >
                  {icon}
                  {label}
                </Button>
              ))}
            </HStack>
          </Box>

          {/* Nutrition bar — desktop only, above stats row */}
          {recipe.nutrition && (
            <NutritionPanel nutrition={recipe.nutrition} variant="desktop" />
          )}

          {/* Bottom row: stats left, Cook mode right */}
          <Box
            display="flex" alignItems="center" justifyContent="space-between"
            borderTopWidth="1px" borderColor="border.muted"
            mt={recipe.nutrition ? 0 : 4} py={3} mx={-8} px={8}
            bg="bg.surface"
          >
            <HStack gap={0}>
              {([
                { label: 'Servings', value: recipe.servings,                 icon: <ServingsIcon size={14} />, tint: 'teal'   as const },
                { label: 'Prep',     value: formatDuration(recipe.prepTime), icon: <TimeIcon size={14} />,     tint: 'teal'   as const },
                { label: 'Cook',     value: formatDuration(recipe.cookTime), icon: <CookIcon size={14} />,     tint: 'bronze' as const },
              ]).map((s, idx) => (
                <HStack
                  key={s.label} gap={2} pr={5} mr={5}
                  borderRightWidth={idx < 2 ? '1px' : '0'}
                  borderColor="border.muted"
                  opacity={!s.value ? 0.45 : 1}
                >
                  <Box color={s.tint === 'teal' ? 'accent.secondary' : 'accent.emphasis'} lineHeight="1">{s.icon}</Box>
                  <Box>
                    <Text fontSize="8px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600">{s.label}</Text>
                    <Text fontSize="13px" fontWeight="600" color="text.primary" lineHeight="1">{s.value || '—'}</Text>
                  </Box>
                </HStack>
              ))}
            </HStack>
            {canUseCookMode && <CookModeToggle variant="band" checked={cookMode} onCheckedChange={setCookMode} />}
          </Box>
        </Box>
      </Box>}

      {/* Body: ingredients | instructions */}
      {!isMobile && <Grid templateColumns="5fr 7fr" minH="300px">
        <GridItem px={7} py={6} borderRightWidth="1px" borderColor="border.muted">
          <HStack justify="space-between" align="center" mb={3} pb={0}>
            <SectionLabel mb={0}>Ingredients</SectionLabel>
            {(recipe.ingredients?.length ?? 0) > 0 && (
              <IconButton aria-label="Copy ingredients" variant="ghost" size="xs" onClick={handleCopyIngredients}>
                <CopyIcon size={15} />
              </IconButton>
            )}
          </HStack>
          <IngredientList ingredients={recipe.ingredients} recipeId={recipeId} />
        </GridItem>
        <GridItem px={8} py={6}>
          <SectionLabel>Instructions</SectionLabel>
          <InstructionList instructions={recipe.instructions} />
        </GridItem>
      </Grid>}

      {/* ── Hero (mobile only) ───────────────────────────────── */}
      {isMobile && <Box
        position="relative" h="300px" overflow="hidden" flexShrink={0}
        style={{ background: 'linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)' }}
      >
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={recipe.title}
            w="100%" h="100%"
            objectFit="cover"
            cursor="pointer"
            onClick={() => setIsImageExpanded(true)}
            style={{ filter: 'saturate(0.75) brightness(0.88)' }}
          />
        ) : (
          <Box
            w="100%" h="100%"
            style={{ background: 'linear-gradient(135deg, #e8a020 0%, #d4741a 50%, #C85A1E 100%)' }}
          />
        )}

        {/* Gradient overlay — darkens top (for icons) and bottom (for text) */}
        <Box
          position="absolute" inset={0}
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 35%, transparent 50%, rgba(0,0,0,0.6) 70%, rgba(0,0,0,0.85) 100%)' }}
          pointerEvents="none"
        />

        {/* ── Floating nav row ── */}
        <HStack
          position="absolute"
          top={0} left={0} right={0}
          px={3}
          pt="calc(env(safe-area-inset-top, 0px) + 12px)"
          justify="space-between"
          align="center"
          zIndex={10}
        >
          <IconButton
            aria-label="Back"
            variant="ghost"
            size="md"
            borderRadius="full"
            color="white"
            _hover={{ bg: 'rgba(255,255,255,0.15)' }}
            onClick={() => void handleBack()}
          >
            <BackIcon size={24} />
          </IconButton>

          {/* Actions */}
          <HStack gap={2}>
            <IconButton
              aria-label="Add to collection"
              variant="ghost"
              size="md"
              borderRadius="full"
              color="white"
              _hover={{ bg: 'rgba(255,255,255,0.15)' }}
              onClick={onCollectionsOpen}
            >
              <AddRecipesIcon size={24} />
            </IconButton>

            <IconButton
              aria-label="Share recipe"
              variant="ghost"
              size="md"
              borderRadius="full"
              color="white"
              _hover={{ bg: 'rgba(255,255,255,0.15)' }}
              onClick={() => void handleShare()}
            >
              <ShareIcon size={24} />
            </IconButton>

            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton
                  aria-label="More actions"
                  variant="ghost"
                  size="md"
                  borderRadius="full"
                  color="white"
                  _hover={{ bg: 'rgba(255,255,255,0.15)' }}
                >
                  <MoreIcon size={24} />
                </IconButton>
              </MenuTrigger>
              <HeaderMenu>
                <RecipeActionsMenu
                  onEdit={() => window.location.assign(`/recipes/${recipe.id}/edit`)}
                  onDelete={() => void handleDelete()}
                  onChangePhoto={() => setShowChangePhoto(true)}
                />
              </HeaderMenu>
            </MenuRoot>
          </HStack>
        </HStack>

        {/* ── Title / rating / tags ── */}
        <Box position="absolute" bottom={0} left={0} right={0} p={5}>
          <HStack gap={1} mb={2}>
            {[1, 2, 3, 4, 5].map(i => (
              <Box
                key={i}
                as="button"
                bg="transparent"
                border="none"
                p="2px"
                cursor="pointer"
                color={i <= Math.floor(recipe.rating || 0) ? '#f5c05a' : 'rgba(255,255,255,0.65)'}
                fontSize="22px"
                lineHeight="1"
                transition="transform 0.1s, color 0.1s"
                style={{ textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}
                _hover={{ transform: 'scale(1.2)', color: '#f5c05a' }}
                onClick={() => void handleUpdateRating(i)}
                aria-label={`Rate ${i} star${i !== 1 ? 's' : ''}`}
              >
                ★
              </Box>
            ))}
          </HStack>

          <Heading
            fontFamily="heading"
            fontWeight="800"
            color="white"
            fontSize="2xl"
            lineHeight="1.08"
            letterSpacing="-0.02em"
            mb={addedByName ? 1 : 2}
          >
            {recipe.title}
          </Heading>

          {addedByName && (
            <Text
              fontFamily="body"
              fontWeight="400"
              fontSize="12px"
              color="rgba(255,255,255,0.75)"
              mb={2}
            >
              Added by {addedByName}
            </Text>
          )}

          {importSource && <RecipeSourceBadge source={importSource} variant="hero" />}

          {/* Tags wrap on the left; Cook mode stays pinned to the right */}
          <HStack align="center" gap={3}>
            {recipe.tags && recipe.tags.length > 0 && (
              <HStack gap={2} flexWrap="wrap" flex={1} minW={0}>
                {recipe.tags.map((tag: string, i: number) => (
                  <Box
                    key={i}
                    fontSize="11px"
                    fontWeight="500"
                    px={3}
                    py="3px"
                    borderRadius="full"
                    border="1px solid"
                    borderColor="rgba(255,255,255,0.35)"
                    color="white"
                    style={{ backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.32)' }}
                  >
                    {tag}
                  </Box>
                ))}
              </HStack>
            )}
            {canUseCookMode && (
              // Bottom-aligned and pulled into the hero's padding so it sits just above the stats bar, near the edge
              <Box ml="auto" flexShrink={0} alignSelf="flex-end" mb={-2} mr={-2}>
                <CookModeToggle variant="hero" checked={cookMode} onCheckedChange={setCookMode} />
              </Box>
            )}
          </HStack>
        </Box>
      </Box>}

      {/* ── Mobile: stat tiles only ──────────────────────────── */}
      {isMobile && <Box>
        <Box bg="bg.surface" borderBottomWidth="1px" borderColor="border.muted" px={4} py={3}>
          <Grid templateColumns="1fr 1fr 1fr" gap={2}>
            <StatTile label="Servings" value={recipe.servings} icon={<ServingsIcon size={14} />} tint="teal" />
            <StatTile label="Prep" value={formatDuration(recipe.prepTime)} icon={<TimeIcon size={14} />} tint="teal" />
            <StatTile label="Cook" value={formatDuration(recipe.cookTime)} icon={<CookIcon size={14} />} tint="bronze" />
          </Grid>
        </Box>
      </Box>}

      {/* ── Mobile: tabs + content ───────────────────────────── */}
      {isMobile && <Box data-testid="mobile-tab-content">
        {/* Sticky tab bar */}
        <HStack
          gap={0}
          borderBottomWidth="1px"
          borderColor="border.muted"
          bg="bg.canvas"
          position="sticky"
          top="0"
          zIndex={50}
        >
          {([
            'ingredients',
            'instructions',
            ...(recipe.nutrition?.perServing ? ['nutrition'] : []),
          ] as ('ingredients' | 'instructions' | 'nutrition')[]).map((tab) => (
            <Box
              key={tab}
              as="button"
              flex={1}
              py={3}
              fontSize="13px"
              fontWeight={mobileTab === tab ? '600' : '500'}
              color={mobileTab === tab ? 'accent.emphasis' : 'text.muted'}
              borderBottomWidth="2px"
              borderColor={mobileTab === tab ? 'accent.emphasis' : 'transparent'}
              mb="-1px"
              bg="transparent"
              border="none"
              cursor="pointer"
              textTransform="capitalize"
              letterSpacing="0.02em"
              onClick={() => setMobileTab(tab)}
            >
              <HStack gap={3} justify="center" align="center">
                <span>{tab}</span>
                {tab === 'ingredients' && (recipe.ingredients?.length ?? 0) > 0 && (
                  <Box
                    as="span"
                    display="inline-flex"
                    onClick={(e: React.MouseEvent) => { e.stopPropagation(); handleCopyIngredients(); }}
                    color={mobileTab === 'ingredients' ? 'accent.emphasis' : 'text.muted'}
                    _hover={{ color: 'accent.emphasis' }}
                    p="2px"
                  >
                    <CopyIcon size={16} />
                  </Box>
                )}
              </HStack>
            </Box>
          ))}
        </HStack>

        {/* Tab content */}
        {mobileTab !== 'nutrition' && (
          <Box px={4} pt={5} pb={8}>
            {mobileTab === 'ingredients' && (
              <IngredientList ingredients={recipe.ingredients} recipeId={recipeId} />
            )}
            {mobileTab === 'instructions' && (
              <InstructionList instructions={recipe.instructions} />
            )}
          </Box>
        )}
        {mobileTab === 'nutrition' && recipe.nutrition && (
          <NutritionPanel nutrition={recipe.nutrition} variant="mobile" />
        )}
      </Box>}

    </MotionBox>

    {/* ── Fixed/portal elements outside MotionBox ──────────────────────────
        position:fixed breaks when a CSS ancestor has a transform applied.
        The MotionBox uses translateX for the push-slide transition, so any
        fixed children inside it are positioned relative to the MotionBox
        instead of the viewport. Everything below must live outside it. */}

    <FloatingAIChefButton recipeId={recipe.id} />

    <BottomNavBar recipeId={recipe.id} />

    <ChangePhotoDialog isOpen={showChangePhoto} onClose={() => setShowChangePhoto(false)} recipeId={recipe.id} />

    <CollectionsDialog
      open={isCollectionsOpen}
      onOpenChange={(open: boolean) => open ? onCollectionsOpen() : onCollectionsClose()}
      collections={collections || []}
      isRecipeInCollection={isRecipeInCollection}
      addToCollection={(id: string) => void addToCollection(id)}
      pending={isAddingToCollection}
      onCreateAndAdd={(name: string) => void createAndAdd(name)}
      createPending={isCreatingAndAdding}
    />

    {/* Full-screen image modal */}
    {isImageExpanded && imageUrl && (
      <Box
        position="fixed" top={0} left={0} right={0} bottom={0}
        style={{ background: 'rgba(0,0,0,0.92)' }}
        zIndex={10000}
        display="flex" alignItems="center" justifyContent="center"
        onClick={() => setIsImageExpanded(false)}
        cursor="pointer"
      >
        <Image
          src={imageUrl}
          alt={recipe.title}
          maxW="95vw" maxH="95vh"
          objectFit="contain"
          onClick={(e) => e.stopPropagation()}
        />
        <Box position="absolute" top={4} right={4} color="white" fontSize="3xl" fontWeight="bold" cursor="pointer" p={2} _hover={{ opacity: 0.7 }}>×</Box>
      </Box>
    )}
    </>
  );
}

// ── Sub-components ───────────────────────────────────────────

function NotFound({ navigate }: { navigate: (path: string) => void }) {
  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: "58px", lg: 0 }}>
      <Header title="Recipe Not Found" showBackButton={true} onBackClick={() => navigate(ROUTES.RECIPES_BASE())} />
      <Box px={4} py={20} textAlign="center">
        <VStack gap={4}>
          <Box fontSize="6xl">🍽️</Box>
          <Heading fontFamily="heading" fontWeight="800" color="text.primary">Recipe not found</Heading>
          <Text color="text.muted" fontWeight="300">The recipe you're looking for doesn't exist or has been removed.</Text>
          <Button bg="accent.emphasis" color="white" _hover={{ bg: 'accent.emphasisHover' }} onClick={() => navigate(ROUTES.RECIPES_BASE())}>Back to Recipes</Button>
        </VStack>
      </Box>
      <BottomNavBar />
    </Box>
  );
}

function Loading() {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  return (
    <Box minH="100vh" bg="bg.canvas" pb={{ base: "58px", lg: 0 }}>

      {/* Desktop skeleton */}
      {!isMobile && <Box>
        {/* Header band */}
        <Box display="flex" borderBottomWidth="1px" borderColor="border.muted" bg="bg.canvas">
          <Skeleton w="200px" minH="220px" flexShrink={0} borderRadius={0} />
          <Box flex={1} px={8} pt={6} pb={0} display="flex" flexDirection="column" justifyContent="space-between">
            <Box>
              <Skeleton h="9px" w="44px" mb={3} borderRadius="sm" />
              <Skeleton h="34px" w="55%" mb={3} borderRadius="md" />
              <Skeleton h="16px" w="120px" mb={3} borderRadius="sm" />
              <HStack gap={2}>
                {['64px', '80px', '72px', '56px'].map((w, i) => (
                  <Skeleton key={i} h="22px" w={w} borderRadius="full" />
                ))}
              </HStack>
            </Box>
            <Box
              display="flex" alignItems="center" justifyContent="space-between"
              borderTopWidth="1px" borderColor="border.muted"
              py={3} mx={-8} px={8} bg="bg.surface"
            >
              <HStack gap={5}>
                {['56px', '64px', '64px'].map((w, i) => <Skeleton key={i} h="28px" w={w} borderRadius="sm" />)}
              </HStack>
              <Skeleton h="40px" w="140px" borderRadius="lg" />
            </Box>
          </Box>
        </Box>

        {/* Body */}
        <Grid templateColumns="5fr 7fr" minH="300px">
          <GridItem px={7} py={6} borderRightWidth="1px" borderColor="border.muted">
            <Skeleton h="12px" w="90px" mb={5} borderRadius="sm" />
            <VStack gap={3} align="stretch">
              {[90, 65, 80, 55, 75, 60].map((w, i) => (
                <Skeleton key={i} h="13px" w={`${w}%`} borderRadius="sm" />
              ))}
            </VStack>
          </GridItem>
          <GridItem px={8} py={6}>
            <Skeleton h="12px" w="90px" mb={5} borderRadius="sm" />
            <VStack gap={4} align="stretch">
              {[100, 85, 100, 70].map((w, i) => (
                <Skeleton key={i} h="36px" w={`${w}%`} borderRadius="sm" />
              ))}
            </VStack>
          </GridItem>
        </Grid>
      </Box>}

      {/* Mobile skeleton */}
      {isMobile && <Box>
        {/* Hero */}
        <Skeleton h="300px" w="100%" borderRadius={0} />
        {/* Stat tiles */}
        <Box bg="bg.surface" borderBottomWidth="1px" borderColor="border.muted" px={4} py={3}>
          <Grid templateColumns="1fr 1fr 1fr" gap={2}>
            {[0, 1, 2].map(i => (
              <Box key={i} bg="bg.canvas" borderRadius="lg" p={3} display="flex" flexDirection="column" gap={1}>
                <Skeleton h="10px" w="50px" borderRadius="sm" />
                <Skeleton h="18px" w="40px" borderRadius="sm" />
              </Box>
            ))}
          </Grid>
        </Box>
        {/* Tab bar */}
        <HStack gap={0} borderBottomWidth="1px" borderColor="border.muted" bg="bg.canvas">
          {['Ingredients', 'Instructions'].map((_, i) => (
            <Box key={i} flex={1} py={3} px={4} borderBottomWidth="2px" borderColor={i === 0 ? 'accent.emphasis' : 'transparent'}>
              <Skeleton h="11px" w="70px" borderRadius="sm" />
            </Box>
          ))}
        </HStack>
        {/* Content */}
        <Box px={4} py={4}>
          <VStack gap={3} align="stretch">
            {[85, 60, 75, 50, 80, 65, 70, 55].map((w, i) => (
              <Skeleton key={i} h="13px" w={`${w}%`} borderRadius="sm" />
            ))}
          </VStack>
        </Box>
        <BottomNavBar />
      </Box>}

    </Box>
  );
}

function SectionLabel({ children, mb = 3 }: { children: React.ReactNode; mb?: number }) {
  return (
    <Box
      as="div"
      fontFamily="heading"
      fontWeight="800"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="text.muted"
      mb={mb}
      pb={2}
      borderBottomWidth="2px"
      borderColor="accent.emphasis"
      display="inline-block"
    >
      {children}
    </Box>
  );
}

function InstructionList({ instructions }: { instructions?: string[] }) {
  if (!instructions || instructions.length === 0) {
    return <Text fontSize="sm" color="text.muted" fontWeight="300">No instructions provided.</Text>;
  }
  return (
    <VStack gap={4} align="stretch">
      {instructions.map((instruction: string, i: number) => (
        <HStack key={i} gap={3} align="flex-start">
          <Box
            w="26px" h="26px"
            borderRadius="full"
            bg="accent.secondary"
            color="white"
            fontSize="11px"
            fontWeight="600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            mt="2px"
          >
            {i + 1}
          </Box>
          <Text fontSize="14px" fontWeight="300" color="text.primary" lineHeight="tall" flex="1">
            {renderInstructionText(instruction, i)}
          </Text>
        </HStack>
      ))}
    </VStack>
  );
}

function renderInstructionText(
  instruction: string,
  stepIndex: number,
): (string | JSX.Element)[] {
  const nodes: (string | JSX.Element)[] = [];
  const boldRegex = /\[(.+?)\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let partIndex = 0;
  while ((match = boldRegex.exec(instruction)) !== null) {
    if (match.index > lastIndex) nodes.push(instruction.slice(lastIndex, match.index));
    nodes.push(<Text as="span" key={`bold-${stepIndex}-${partIndex++}`} fontWeight="600">({match[1].trim()})</Text>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < instruction.length) nodes.push(instruction.slice(lastIndex));
  return nodes;
}

interface StatTileProps {
  label: string;
  value?: string | number | null;
  icon: JSX.Element;
  tint: 'teal' | 'bronze';
}

function StatTile({ label, value, icon, tint }: StatTileProps) {
  const isTeal = tint === 'teal';
  const isEmpty = !value;
  return (
    <VStack
      gap="2px"
      align="center"
      py={2}
      px={2}
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.muted"
      borderRadius="lg"
      opacity={isEmpty ? 0.45 : 1}
      data-testid={isEmpty ? undefined : `stat-${label.toLowerCase()}`}
    >
      <Box color={isTeal ? 'accent.secondary' : 'accent.emphasis'} fontSize="16px" lineHeight="1">
        {icon}
      </Box>
      <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.08em" color="text.muted" fontWeight="600">{label}</Text>
      <Text fontSize="13px" fontWeight="600" color={isEmpty ? 'text.muted' : 'text.primary'} lineHeight="1">
        {isEmpty ? '—' : value}
      </Text>
    </VStack>
  );
}

